import React, { useState, useEffect, useRef, useMemo, useCallback, lazy, Suspense } from 'react';
import T from '../styles/theme';
import { Avatar, fmtDollar , Checkbox} from '../components/UI';
import Icon from '../components/Icon';
import { useStore } from '../data/store';
import { useAuth } from '../data/auth';
import { useToast } from '../components/Toast';
import apiFetch from '../data/api';
import supabase from '../data/supabase';
import CoreOrb from '../components/CoreOrb';
import MCTheme from '../components/MCTheme';

// ── Simple markdown renderer for Core AI responses ──
function renderCoreMessage(text) {
  if (!text) return null;
  // Pre-process: force line breaks before numbered items like "**1." or "1." mid-text
  let processed = text
    .replace(/\s*\*\*(\d+)\./g, '\n**$1.')
    .replace(/([.!?])\s+(\d+)\.\s/g, '$1\n$2. ')
    .replace(/\*\*\s*—\s*/g, '** — ');

  const lines = processed.split("\n");
  const elements = [];
  let i = 0;

  for (const line of lines) {
    const key = i++;
    const trimmed = line.trim();
    if (!trimmed) { elements.push(<div key={key} style={{ height: 6 }} />); continue; }

    // Section headers: lines ending with ":" that are short
    if (trimmed.endsWith(":") && trimmed.length < 60 && !trimmed.includes("—")) {
      elements.push(
        <div key={key} style={{ fontSize: 10, fontWeight: 400, color: "rgba(217,219,221,0.4)", letterSpacing: ".04em", textTransform: "uppercase", marginTop: i > 1 ? 10 : 0, marginBottom: 4 }}>
          {renderInline(trimmed.replace(/:$/, ""))}
        </div>
      );
      continue;
    }

    // Numbered list items: "1. Something" or "**1. Something**"
    const numMatch = trimmed.match(/^\*{0,2}(\d+)\.\s*\*{0,2}\s*(.+)/);
    if (numMatch) {
      elements.push(
        <div key={key} style={{ display: "flex", gap: 10, marginBottom: 8, alignItems: "flex-start", padding: "8px 10px", borderRadius: 8, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(217,219,221,0.06)" }}>
          <span style={{ color: "rgba(250,250,250,0.5)", fontSize: 13, fontWeight: 400, minWidth: 18, flexShrink: 0, textAlign: "center" }}>{numMatch[1]}</span>
          <span style={{ flex: 1, lineHeight: 1.6 }}>{renderInline(numMatch[2])}</span>
        </div>
      );
      continue;
    }

    // Bullet points: "- Something" or "• Something"
    if (trimmed.startsWith("- ") || trimmed.startsWith("• ")) {
      elements.push(
        <div key={key} style={{ display: "flex", gap: 8, marginBottom: 4, alignItems: "flex-start", paddingLeft: 6 }}>
          <span style={{ color: "rgba(217,219,221,0.25)", fontSize: 6, marginTop: 6 }}>●</span>
          <span style={{ flex: 1 }}>{renderInline(trimmed.slice(2))}</span>
        </div>
      );
      continue;
    }

    // Regular line
    elements.push(<div key={key} style={{ marginBottom: 4 }}>{renderInline(trimmed)}</div>);
  }

  return elements;
}

// Inline formatting: **bold**, *italic*
function renderInline(text) {
  if (!text) return null;
  const parts = [];
  let remaining = text;
  let key = 0;

  while (remaining.length > 0) {
    // Bold: **text**
    const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
    if (boldMatch) {
      const before = remaining.slice(0, boldMatch.index);
      if (before) parts.push(<span key={key++}>{before}</span>);
      parts.push(<span key={key++} style={{ color: "#FAFAFA", fontWeight: 400 }}>{boldMatch[1]}</span>);
      remaining = remaining.slice(boldMatch.index + boldMatch[0].length);
      continue;
    }
    // No more formatting
    parts.push(<span key={key++}>{remaining}</span>);
    break;
  }

  return parts;
}
import processCommand from '../data/CoreCommands';

/* ═══════════════════════════════════════════════════
   MISSION CONTROL V13 — VOICE + BRIEFING
   Spacebar push-to-talk · Daily briefing panel
   Contact/Deal modals · Full voice commands
   ═══════════════════════════════════════════════════ */

const SMSPage = lazy(() => import('./SMSPage'));
const EmailPage = lazy(() => import('./EmailPage'));
const DialerPage = lazy(() => import('./DialerPage'));
const CalendarPage = lazy(() => import('./CalendarPage'));
const TeamChatPage = lazy(() => import('./TeamChatPage'));
const ProjectsPage = lazy(() => import('./ProjectsPage'));
const CallLogsPage = lazy(() => import('./CallLogsPage'));
const WorkflowsPage = lazy(() => import('./WorkflowsPage'));
const ContactRecord = lazy(() => import('./ContactRecord'));
const DealRecord = lazy(() => import('./DealRecord'));

const A = "#4059AD", AL = "#6B83D4";

// Classify an activity into a date bucket relative to "now" in the
// user's local timezone. Returns "today" / "yesterday" / "thisWeek" /
// "thisMonth" / "older". Boundaries are computed via Intl.DateTimeFormat
// in the target tz so users in non-UTC timezones see "today" aligned
// to their local midnight, not UTC midnight.
//
// Week starts on Monday (ISO week). "thisWeek" excludes today and
// yesterday — those get their own buckets. "thisMonth" excludes the
// other three — only days earlier in the calendar month than the
// current ISO week's Monday land here.
function bucketActivity(createdAtIso, userTz) {
  if (!createdAtIso) return "older";
  const tz = userTz || "America/New_York";
  const now = new Date();
  let actDate;
  try { actDate = new Date(createdAtIso); }
  catch { return "older"; }
  if (isNaN(actDate.getTime())) return "older";

  // Format any Date as "YYYY-MM-DD" in the target tz. en-CA gives
  // ISO-8601 ordering by default, which makes string compare === date compare.
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit",
  });
  const todayStr = fmt.format(now);
  const yesterdayStr = fmt.format(new Date(now.getTime() - 86400000));
  const activityDateStr = fmt.format(actDate);

  if (activityDateStr === todayStr) return "today";
  if (activityDateStr === yesterdayStr) return "yesterday";

  // Compute current week's Monday in user's tz as a YYYY-MM-DD string.
  // We parse todayStr's components as a NAIVE local date to do day-of-week
  // arithmetic — getDay() on a naive Date is timezone-stable here because
  // we built the date from the year/month/day strings already in user-tz.
  const dayOfWeek = (yyyymmdd) => {
    const [y, m, d] = yyyymmdd.split("-").map(Number);
    return new Date(y, m - 1, d).getDay(); // 0=Sun..6=Sat
  };
  const daysSinceMonday = (yyyymmdd) => {
    const dow = dayOfWeek(yyyymmdd);
    return dow === 0 ? 6 : dow - 1; // Sun → 6, Mon → 0
  };
  const mondayStr = (() => {
    const [y, m, d] = todayStr.split("-").map(Number);
    const dt = new Date(y, m - 1, d);
    dt.setDate(dt.getDate() - daysSinceMonday(todayStr));
    const yy = dt.getFullYear();
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    const dd = String(dt.getDate()).padStart(2, "0");
    return `${yy}-${mm}-${dd}`;
  })();

  // "thisWeek" is anything between Monday-of-this-week and yesterday inclusive.
  // We've already returned for today/yesterday above, so just check >= Monday.
  if (activityDateStr >= mondayStr && activityDateStr < yesterdayStr) return "thisWeek";

  // Same calendar month but earlier than this week's Monday.
  const todayMonth = todayStr.slice(0, 7);
  const activityMonth = activityDateStr.slice(0, 7);
  if (activityMonth === todayMonth) return "thisMonth";

  return "older";
}

// Returns ISO timestamp for midnight today in the given timezone, as a
// string with explicit offset (e.g. "2026-05-04T00:00:00-04:00").
// Postgres parses this correctly as a TIMESTAMPTZ, so it can be passed
// directly to .gte() / .lte() filters. Used by Key Metrics card queries
// so "today" cuts over at the user's local midnight, not UTC midnight.
function localTodayStartIso(userTz) {
  const tz = userTz || "America/New_York";
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit",
  });
  const todayStr = fmt.format(new Date()); // "2026-05-04"
  // Pull the tz's current UTC offset via Intl longOffset, e.g. "GMT-04:00".
  const offsetFmt = new Intl.DateTimeFormat("en-US", {
    timeZone: tz, timeZoneName: "longOffset",
  });
  const parts = offsetFmt.formatToParts(new Date());
  const tzPart = parts.find(p => p.type === "timeZoneName")?.value || "GMT+00:00";
  const offset = tzPart.replace("GMT", "") || "+00:00"; // "-04:00"
  return `${todayStr}T00:00:00${offset}`;
}
const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

/* ── Edge Panel ── */
function EdgePanel({ side="right", visible, onClose, title, children, width=340 }) {
  if (!visible) return null;
  const isLeft=side==="left",isTop=side==="top";
  const horiz=isTop?{}:isLeft?{left:0}:{right:0};
  const transform=!visible?(isTop?"translateY(-100%)":isLeft?"translateX(-100%)":"translateX(100%)"):"translate(0)";
  return(<div style={{position:"fixed",inset:0,zIndex:1500,pointerEvents:visible?"auto":"none",overflow:"hidden"}}><div onClick={onClose} style={{position:"absolute",inset:0,background:visible?"rgba(0,0,0,0.50)":"transparent",backdropFilter:visible?"blur(4px)":"none",transition:"all .35s ease"}}/><div style={{position:"absolute",...horiz,top:isTop?0:16,bottom:isTop?"auto":16,width:isTop?"100%":width,maxHeight:isTop?"60vh":"auto",background:"#2C2E33",border:"1px solid rgba(217,219,221,0.12)",borderRadius:isTop?"0 0 24px 24px":isLeft?"0 24px 24px 0":"24px 0 0 24px",boxShadow:`0 30px 80px rgba(0,0,0,0.40),0 0 0 1px rgba(217,219,221,0.08)`,transform,transition:"transform .45s cubic-bezier(.32,1,.23,1)",overflow:"hidden"}}><div style={{padding:"16px 22px",display:"flex",justifyContent:"space-between",alignItems:"center",borderBottom:"1px solid rgba(217,219,221,0.12)"}}><span style={{fontSize:14,fontWeight:400,color:T.txt}}>{title}</span><button onClick={onClose} style={{width:26,height:26,borderRadius:8,border:"1px solid rgba(217,219,221,0.10)",background:"transparent",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,color:T.tx3}}>×</button></div><div style={{padding:"14px 20px",overflowY:"auto",maxHeight:isTop?"calc(60vh - 54px)":"calc(100vh - 86px)"}}>{children}</div></div></div>);
}

/* ── System Modal ── */
function SystemModal({ title, onClose, children, size="large" }) {
  const [v,setV]=useState(false);
  useEffect(()=>{requestAnimationFrame(()=>requestAnimationFrame(()=>setV(true)));},[]);
  const close=()=>{setV(false);setTimeout(onClose,300);};
  const dims=size==="full"?{width:"96vw",height:"96vh"}:size==="large"?{width:"96vw",maxWidth:1200,height:"92vh"}:{width:"80vw",maxWidth:700,height:"75vh"};
  return(<div style={{position:"fixed",inset:0,zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center"}}><div onClick={close} style={{position:"absolute",inset:0,background:v?"rgba(0,0,0,0.85)":"transparent",transition:"all .4s ease"}}/><div style={{position:"relative",...dims,background:"#2C2E33",border:"1px solid rgba(217,219,221,0.12)",borderRadius: 8,overflow:"hidden",boxShadow:`0 50px 120px rgba(0,0,0,0.40),0 0 0 1px rgba(217,219,221,0.08)`,transform:v?"scale(1) translateY(0)":"scale(0.88) translateY(24px)",opacity:v?1:0,transition:"all .5s cubic-bezier(.32,1,.23,1)",display:"flex",flexDirection:"column"}}><div style={{padding:"12px 22px",display:"flex",justifyContent:"space-between",alignItems:"center",borderBottom:"1px solid rgba(217,219,221,0.12)",flexShrink:0}}><span style={{fontSize:14,fontWeight:400,color:T.txt}}>{title}</span><button onClick={close} style={{width:28,height:28,borderRadius:9,border:"1px solid rgba(217,219,221,0.10)",background:"transparent",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",fontSize:15,color:T.tx3}} onMouseEnter={e=>e.currentTarget.style.background="rgba(217,219,221,0.08)"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>×</button></div><div style={{flex:1,overflow:"hidden",position:"relative"}}><Suspense fallback={<div style={{display:"flex",alignItems:"center",justifyContent:"center",height:200}}><div style={{display:"flex",gap:4}}>{[0,1,2].map(i=><div key={i} style={{width:6,height:6,borderRadius:3,background:AL,animation:`imPulse .6s ease ${i*.12}s infinite`}}/>)}</div></div>}>{children}</Suspense></div></div></div>);
}

/* ── Small Center Modal ── */
function CenterModal({ title, onClose, children, width=480 }) {
  const [v,setV]=useState(false);
  useEffect(()=>{requestAnimationFrame(()=>requestAnimationFrame(()=>setV(true)));},[]);
  const close=()=>{setV(false);setTimeout(onClose,300);};
  return(<div style={{position:"fixed",inset:0,zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center"}}><div onClick={close} style={{position:"absolute",inset:0,background:v?"rgba(0,0,0,0.85)":"transparent",transition:"all .35s ease"}}/><div style={{position:"relative",width,maxWidth:"92vw",maxHeight:"82vh",background:"#2C2E33",border:"1px solid rgba(217,219,221,0.12)",borderRadius: 8,overflow:"hidden",boxShadow:`0 40px 100px rgba(0,0,0,0.40),0 0 0 1px rgba(217,219,221,0.08)`,transform:v?"scale(1)":"scale(0.92) translateY(12px)",opacity:v?1:0,transition:"all .4s cubic-bezier(.32,1,.23,1)"}}><div style={{padding:"16px 22px",display:"flex",justifyContent:"space-between",alignItems:"center",borderBottom:"1px solid rgba(217,219,221,0.12)"}}><span style={{fontSize:14,fontWeight:400,color:T.txt}}>{title}</span><button onClick={close} style={{width:26,height:26,borderRadius:8,border:"1px solid rgba(217,219,221,0.10)",background:"transparent",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,color:T.tx3}}>×</button></div><div style={{maxHeight:"calc(82vh - 54px)",overflowY:"auto"}}>{children}</div></div></div>);
}

function Ring({ pct, size=44, stroke=3, color, children }) {
  const r=(size-stroke)/2,circ=2*Math.PI*r;const [ap,setAp]=useState(0);useEffect(()=>{setTimeout(()=>setAp(pct),150);},[pct]);
  return(<div style={{position:"relative",width:size,height:size}}><svg width={size} height={size} style={{transform:"rotate(-90deg)"}}><circle cx={size/2} cy={size/2} r={r} fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth={stroke}/><circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={stroke} strokeDasharray={circ} strokeDashoffset={circ-(circ*Math.min(ap,100)/100)} strokeLinecap="round" style={{transition:"stroke-dashoffset 1.4s cubic-bezier(.16,1,.3,1)",filter:`drop-shadow(0 0 4px ${color}40)`}}/></svg><div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center"}}>{children}</div></div>);
}

function CoreHistoryPanel({ coreMessages, auth, supabase, renderCoreMessage, setCoreMessages }) {
  const [history, setHistory] = React.useState([]);
  const [loaded, setLoaded] = React.useState(false);
  const [clearing, setClearing] = React.useState(false);
  const toast = useToast();
  React.useEffect(() => {
    const userId = auth.currentUser?._dbId;
    if (!userId) return;
    supabase.from("core_chat_history").select("messages").eq("user_id", userId).maybeSingle()
      .then(({ data }) => { if (data?.messages) setHistory(data.messages); setLoaded(true); })
      .catch(() => setLoaded(true));
  }, [auth.currentUser?._dbId]);

  const clearHistory = async () => {
    const userId = auth.currentUser?._dbId;
    if (!userId) { toast?.error?.("Not signed in"); return; }
    setClearing(true);
    try {
      const { error } = await supabase.from("core_chat_history").delete().eq("user_id", userId);
      if (error) { toast?.error?.("Failed to clear history: " + error.message); return; }
      // Only clear local state after successful DB delete
      setCoreMessages([]);
      setHistory([]);
      toast?.success?.("History cleared");
    } catch (err) {
      toast?.error?.("Failed to clear history: " + (err.message || "unknown error"));
    } finally {
      setClearing(false);
    }
  };
  // Merge: saved history + current session
  const all = [...history.filter(h => !coreMessages.some(m => m.text === h.text && m.role === h.role)), ...coreMessages];
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 140px)" }}>
      <div style={{ flex: 1, overflowY: "auto", marginBottom: 10 }}>
        {all.length === 0 ? <div style={{ padding: 20, textAlign: "center", color: "rgba(217,219,221,0.4)", fontSize: 12 }}>No conversations yet. Ask Core anything below.</div> : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {all.map((msg, i) => (
              <div key={i} style={{ display: "flex", justifyContent: msg.role === "user" ? "flex-end" : "flex-start" }}>
                <div style={{ maxWidth: "90%", padding: msg.role === "user" ? "6px 10px" : "8px 12px", fontSize: 11, lineHeight: 1.5, color: msg.role === "user" ? "#FAFAFA" : "rgba(217,219,221,0.65)", background: msg.role === "user" ? "#4059AD" : "rgba(255,255,255,0.03)", border: msg.role === "user" ? "none" : "1px solid rgba(217,219,221,0.06)", borderRadius: 8 }}>
                  {msg.role === "user" ? msg.text : renderCoreMessage(msg.text)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <div style={{ borderTop: "1px solid rgba(217,219,221,0.08)", paddingTop: 10, flexShrink: 0 }}>
        <button onClick={clearHistory} disabled={clearing}
          style={{ width: "100%", padding: "8px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.08)", background: "transparent", color: "rgba(217,219,221,0.35)", fontSize: 10, cursor: clearing ? "default" : "pointer", fontFamily: "inherit", opacity: clearing ? 0.6 : 1 }}>
          {clearing ? "Clearing..." : "Clear History"}
        </button>
      </div>
    </div>
  );
}

export default function Dashboard({ setPage, onSelectContact, onSelectDeal, isMobile }) {
  const store = useStore();
  const auth = useAuth();
  const { contacts, deals, tasks, currentUser, currentUserId, currentUserMember, addToQueue, activities, kpiTargets, callQueue } = store;
  const firstName = (currentUser || "").split(" ")[0] || "there";
  const [coreInput, setCoreInput] = useState(""); const [coreMessages, setCoreMessages] = useState([]);
  const [coreLoading, setCoreLoading] = useState(false); const chatRef = useRef(null);
  const coreMsgCountRef = useRef(0);

  // Save chat history to Supabase when messages change
  useEffect(() => {
    const userId = auth.currentUser?._dbId;
    if (!userId || coreMessages.length === 0) return;
    // Skip save on initial load
    if (coreMsgCountRef.current === 0) { coreMsgCountRef.current = coreMessages.length; return; }
    if (coreMessages.length <= coreMsgCountRef.current) { coreMsgCountRef.current = coreMessages.length; return; }
    coreMsgCountRef.current = coreMessages.length;
    const t = setTimeout(async () => {
      try {
        const { data: existing } = await supabase.from("core_chat_history").select("id").eq("user_id", userId).maybeSingle();
        if (existing) {
          await supabase.from("core_chat_history").update({ messages: coreMessages.slice(-50), updated_at: new Date().toISOString() }).eq("user_id", userId);
        } else {
          await supabase.from("core_chat_history").insert({ user_id: userId, messages: coreMessages.slice(-50), updated_at: new Date().toISOString() });
        }
      } catch (e) { /* silently fail */ }
    }, 1000);
    return () => clearTimeout(t);
  }, [coreMessages, auth.currentUser?._dbId]);
  const [greeting, setGreeting] = useState(""); const [booted, setBooted] = useState(false);
  const [coreSpeaking, setCoreSpeaking] = useState(false); const [coreVoiceText, setCoreVoiceText] = useState("");
  const [showChat, setShowChat] = useState(false); const [hoverBar, setHoverBar] = useState(false);
  const [edgePanel, setEdgePanel] = useState(null); const [modal, setModal] = useState(null);
  // My Activity panel: which date buckets are currently expanded.
  // Today defaults to open; the other three default closed and reveal
  // on click. Empty non-today buckets are hidden entirely.
  const [expandedBuckets, setExpandedBuckets] = useState({ today: true, yesterday: false, thisWeek: false, thisMonth: false });
  const toggleBucket = (key) => setExpandedBuckets(prev => ({ ...prev, [key]: !prev[key] }));
  const [coreListening, setCoreListening] = useState(false);
  const [showBriefing, setShowBriefing] = useState(false);
  const [briefingData, setBriefingData] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [selectedDealId, setSelectedDealId] = useState(null);
  const [callTarget, setCallTarget] = useState(null);
  const [disambig, setDisambig] = useState(null); // { contacts: [], intentId, rawText }
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [micPermission, setMicPermission] = useState("unknown"); // unknown, granted, denied, unsupported
  const [apiStatus, setApiStatus] = useState("online"); // online, offline, error
  const [orbError, setOrbError] = useState(false);
  const recognitionRef = useRef(null);
  const spaceHeldRef = useRef(false);
  // Mission Control thread — persists across sessions via localStorage
  const [threadId, setThreadId] = useState(() => localStorage.getItem("cf_mc_thread_id"));
  const [pendingAction, setPendingAction] = useState(null); // { id, tool, input, summary }
  const [toolActivity, setToolActivity] = useState(null);   // e.g. "Looking up Hunter Hopkins..."

  useEffect(() => { window.__coreflow_current_page = "dashboard"; window.__coreflow_open_panel = setModal; return () => { window.__coreflow_current_page = null; }; }, []);
  useEffect(() => { window.scrollTo(0, 0); }, []);
  useEffect(() => { const h = new Date().getHours(); setGreeting(h<12?"Good morning":h<17?"Good afternoon":"Good evening"); }, []);
  useEffect(() => { window.speechSynthesis?.getVoices(); }, []);
  useEffect(() => { const t = setTimeout(() => setBooted(true), 400); return () => clearTimeout(t); }, []);

  // ── Onboarding: show once ever ──
  useEffect(() => {
    if (localStorage.getItem("cf_mc_onboarded")) return;
    setTimeout(() => setShowOnboarding(true), 1500);
  }, []);
  const dismissOnboarding = () => {
    setShowOnboarding(false);
    localStorage.setItem("cf_mc_onboarded", "1");
  };

  // ── Mic permission check ──
  useEffect(() => {
    if (!SpeechRecognition) { setMicPermission("unsupported"); return; }
    navigator.permissions?.query?.({ name: "microphone" }).then(result => {
      setMicPermission(result.state);
      result.onchange = () => setMicPermission(result.state);
    }).catch(() => setMicPermission("unknown"));
  }, []);
  const requestMicPermission = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(t => t.stop());
      setMicPermission("granted");
      return true;
    } catch (e) { setMicPermission("denied"); return false; }
  };

  // ── Online/offline detection ──
  useEffect(() => {
    const goOffline = () => { setApiStatus("offline"); setOrbError(true); };
    const goOnline = () => { setApiStatus("online"); setOrbError(false); };
    window.addEventListener("offline", goOffline);
    window.addEventListener("online", goOnline);
    if (!navigator.onLine) goOffline();
    return () => { window.removeEventListener("offline", goOffline); window.removeEventListener("online", goOnline); };
  }, []);

  // Data
  const today = new Date().toISOString().slice(0, 10);
  const todayTasks = tasks.filter(t => !t.done && t.due === today);
  const overdue = tasks.filter(t => !t.done && t.due && t.due < today);
  const upcoming = tasks.filter(t => !t.done && t.due && t.due > today).sort((a,b)=>a.due.localeCompare(b.due)).slice(0,5);
  const activeDeals = deals.filter(d => d.stage !== "Closed Won" && d.stage !== "Closed Lost");
  const pipe = activeDeals.reduce((s, d) => s + d.val, 0);
  const weighted = activeDeals.reduce((s, d) => s + d.val * (d.prob||50) / 100, 0);
  const won = deals.filter(d => d.stage === "Closed Won").reduce((s, d) => s + d.val, 0);
  const hot = contacts.filter(c => c.score >= 75).sort((a, b) => b.score - a.score).slice(0, 6);
  // todayActs is preserved for CoreCommands voice ctx (the voice agent
  // references org-wide activity volume in its prompt). The Manager
  // Team Activity card no longer reads from it — see the org-wide
  // state vars + useEffect below.
  const todayActs = activities.filter(a => a.date && a.date.startsWith(today));
  const staleDeals = activeDeals.filter(d => (d.days||0) > 14).sort((a, b) => b.days - a.days).slice(0, 5);
  const targets = ((kpiTargets||{})[currentUserMember?.role||"sdr"]) || { dials: 100, emails: 50, linkedin_dms: 0 };
  // Personal "today" counts: per-user, deduped, tz-aware. See useEffect below.
  const [todayCallsMade, setTodayCallsMade] = useState(0);
  const [todayCallsReceived, setTodayCallsReceived] = useState(0);
  const [todayEmailsSent, setTodayEmailsSent] = useState(0);
  // Org-wide "today" counts for the Manager Team Activity card. Same
  // dedup + tz semantics as the per-user counts; just drops the
  // user_id filter. Only loaded when isManager (see useEffect).
  const [orgCallsMade, setOrgCallsMade] = useState(0);
  const [orgCallsReceived, setOrgCallsReceived] = useState(0);
  const [orgEmailsSent, setOrgEmailsSent] = useState(0);
  const dialPct = targets.dials > 0 ? Math.min(100, Math.round((todayCallsMade/targets.dials)*100)) : 0;
  const emailPct = targets.emails > 0 ? Math.min(100, Math.round((todayEmailsSent/targets.emails)*100)) : 0;
  const nowHr = new Date().getHours();
  const hours = Array.from({length:10},(_,i)=>i+8);
  const userRole = currentUserMember?.role || "admin";
  const isManager = userRole === "admin" || userRole === "manager";
  // Connect rate retains the activities-based connectedCalls signal because
  // duration is denormalized onto activity rows; pairing it with the new
  // per-user todayCallsMade keeps the rate user-personal while preserving
  // the existing definition of "connected" (duration > 0).
  const connectedCalls = todayActs.filter(a => a.type === "call" && a.duration).length;
  const connectRate = todayCallsMade > 0 ? Math.round((connectedCalls / todayCallsMade) * 100) : 0;
  const queueSize = (callQueue || []).length;

  // Per-user "today" call/email counts. Fetched once on mount and when
  // the user / org / tz changes. v1: no auto-refresh — counts are
  // accurate at panel-open time.
  const userTz = currentUserMember?.timezone || "America/New_York";
  useEffect(() => {
    const orgId = window.__coreflow_org_id;
    if (!currentUserId || !orgId) return;
    let cancelled = false;
    (async () => {
      const sinceIso = localTodayStartIso(userTz);
      try {
        // Calls — pull both directions in one query, dedup on twilio_sid
        // (or "id:<row.id>" when twilio_sid is NULL), then split by direction.
        const { data: callRows } = await supabase.from("call_logs")
          .select("id, direction, twilio_sid")
          .eq("org_id", orgId)
          .eq("user_id", currentUserId)
          .gte("started_at", sinceIso);
        if (callRows && !cancelled) {
          const seen = new Set();
          let made = 0, received = 0;
          for (const row of callRows) {
            const dedupKey = row.twilio_sid || `id:${row.id}`;
            if (seen.has(dedupKey)) continue;
            seen.add(dedupKey);
            if (row.direction === "outbound") made++;
            else if (row.direction === "inbound") received++;
          }
          setTodayCallsMade(made);
          setTodayCallsReceived(received);
        }
      } catch (e) { console.warn("[CoreFlow] Today call counts load failed:", e?.message); }
      try {
        // Emails — outbound only, head-count for efficiency.
        const { count: emailCount } = await supabase.from("emails")
          .select("id", { count: "exact", head: true })
          .eq("org_id", orgId)
          .eq("user_id", currentUserId)
          .eq("direction", "outbound")
          .gte("created_at", sinceIso);
        if (typeof emailCount === "number" && !cancelled) {
          setTodayEmailsSent(emailCount);
        }
      } catch (e) { console.warn("[CoreFlow] Today email count load failed:", e?.message); }

      // Org-wide Team Activity counts. Manager-gated for two reasons:
      // (1) non-managers don't render the card, so issuing the query
      // would be wasted work; (2) defense in depth — even though RLS
      // would block them, never sending the query in the first place
      // is cleaner than relying on row-level enforcement.
      if (isManager) {
        try {
          const { data: orgCallRows } = await supabase.from("call_logs")
            .select("id, direction, twilio_sid")
            .eq("org_id", orgId)
            .gte("started_at", sinceIso);
          if (orgCallRows && !cancelled) {
            const seen = new Set();
            let made = 0, received = 0;
            for (const row of orgCallRows) {
              const dedupKey = row.twilio_sid || `id:${row.id}`;
              if (seen.has(dedupKey)) continue;
              seen.add(dedupKey);
              if (row.direction === "outbound") made++;
              else if (row.direction === "inbound") received++;
            }
            setOrgCallsMade(made);
            setOrgCallsReceived(received);
          }
        } catch (e) { console.warn("[CoreFlow] Org call counts load failed:", e?.message); }
        try {
          const { count: orgEmailCount } = await supabase.from("emails")
            .select("id", { count: "exact", head: true })
            .eq("org_id", orgId)
            .eq("direction", "outbound")
            .gte("created_at", sinceIso);
          if (typeof orgEmailCount === "number" && !cancelled) {
            setOrgEmailsSent(orgEmailCount);
          }
        } catch (e) { console.warn("[CoreFlow] Org email count load failed:", e?.message); }
      }
    })();
    return () => { cancelled = true; };
  }, [currentUserId, userTz, isManager]);
  const [todayEvents, setTodayEvents] = useState([]);
  useEffect(() => {
    const oid = window.__coreflow_org_id;
    if (!oid) return;
    const startOfDay = today + "T00:00:00";
    const endOfDay = today + "T23:59:59";
    supabase.from("calendar_events").select("*").eq("org_id", oid).gte("start_time", startOfDay).lte("start_time", endOfDay).order("start_time").then(({ data }) => { if (data) setTodayEvents(data); });
  }, [today]);

  // ── Speech ──
  const [voiceEnabled, setVoiceEnabled] = useState(() => {
    try { return localStorage.getItem("cf_voice_enabled") !== "false"; } catch { return true; }
  });
  const toggleVoice = () => {
    setVoiceEnabled(prev => { const next = !prev; localStorage.setItem("cf_voice_enabled", String(next)); if (!next) window.speechSynthesis?.cancel(); return next; });
  };
  const speakingTextRef = useRef("");
  const speak = useCallback((text, onEnd) => { try {
    if (!voiceEnabled || !text) { onEnd?.(); return; }
    // Don't interrupt current speech for the same text
    if (window.speechSynthesis?.speaking && speakingTextRef.current === text) { onEnd?.(); return; }
    window.speechSynthesis?.cancel();
    // Smart truncation: cut at sentence boundary, not mid-word
    let spoken = text;
    if (spoken.length > 500) {
      const cut = spoken.slice(0, 500);
      const lastPeriod = cut.lastIndexOf(". ");
      const lastComma = cut.lastIndexOf(", ");
      spoken = cut.slice(0, Math.max(lastPeriod + 1, lastComma + 1, 200));
    }
    speakingTextRef.current = text;
    const u = new SpeechSynthesisUtterance(spoken);
    u.rate = 0.92; u.pitch = 0.82; u.volume = 0.7;
    const v = window.speechSynthesis?.getVoices() || [];
    const p = v.find(v => /Daniel|Alex|Google UK/.test(v.name));
    if (p) u.voice = p;
    setCoreSpeaking(true); setCoreVoiceText(text);
    u.onend = () => { setCoreSpeaking(false); setCoreVoiceText(""); speakingTextRef.current = ""; onEnd?.(); };
    u.onerror = () => { setCoreSpeaking(false); setCoreVoiceText(""); speakingTextRef.current = ""; onEnd?.(); };
    // Chrome bug workaround: speech can stop randomly after ~15s
    const keepAlive = setInterval(() => {
      if (window.speechSynthesis?.speaking) { window.speechSynthesis.resume(); }
      else { clearInterval(keepAlive); }
    }, 5000);
    const windDown = () => {
      clearInterval(keepAlive);
      speakingTextRef.current = "";
      // Keep speaking state active for 800ms after speech ends
      // so orb glow/displacement winds down gracefully
      setTimeout(() => {
        setCoreSpeaking(false);
        setCoreVoiceText("");
        onEnd?.();
      }, 800);
    };
    u.onend = windDown;
    u.onerror = () => { clearInterval(keepAlive); setCoreSpeaking(false); setCoreVoiceText(""); speakingTextRef.current = ""; onEnd?.(); };
    window.speechSynthesis.speak(u);
  } catch(e) { setCoreSpeaking(false); setCoreVoiceText(""); onEnd?.(); } }, [voiceEnabled]);

  // ── Daily Briefing ──
  useEffect(() => { if(!booted||!firstName||firstName==="there") return; const t = setTimeout(() => {
    const tc=overdue.length+todayTasks.length;
    const ps=pipe>=1e6?(pipe/1e6).toFixed(1)+"M":pipe>=1e3?Math.round(pipe/1e3)+"K":String(pipe);
    const ws=weighted>=1e6?(weighted/1e6).toFixed(1)+"M":weighted>=1e3?Math.round(weighted/1e3)+"K":String(weighted);
    setBriefingData({
      greeting: greeting + ", " + firstName,
      pipeline: fmtDollar(pipe), pipeDeals: activeDeals.length,
      forecast: fmtDollar(weighted), won: fmtDollar(won),
      tasksDue: todayTasks.length, overdue: overdue.length,
      dials: todayCallsMade, emails: todayEmailsSent,
      hotLeads: hot.slice(0,3).map(c=>c.fn+" "+c.ln),
      staleCount: staleDeals.length,
    });
    // Only show briefing + speak every 12 hours
    const lastBriefing = parseInt(localStorage.getItem("cf_last_briefing") || "0", 10);
    const hoursSince = (Date.now() - lastBriefing) / (1000 * 60 * 60);
    if (hoursSince >= 12) {
      localStorage.setItem("cf_last_briefing", String(Date.now()));
      // Briefing data is ready but don't auto-speak — user can trigger via voice command
    }
  }, 900); return ()=>clearTimeout(t); }, [booted]);


  // ── Core AI — Mission Control streaming orchestrator (SSE) ──
  const sendCoreMsgRef = useRef(null);
  const sendCoreMsg = useCallback(async (text, _aiPrompt) => {
    setCoreLoading(true);
    try {
      // Ensure we have a thread
      let tid = threadId;
      if (!tid) {
        const newResp = await apiFetch("/api/ai/core", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "new_thread" }),
        });
        const newData = await newResp.json();
        if (!newData.thread_id) throw new Error("Failed to create thread");
        tid = newData.thread_id;
        setThreadId(tid);
        localStorage.setItem("cf_mc_thread_id", tid);
      }

      // POST send_message and consume the SSE stream
      const resp = await apiFetch("/api/ai/core", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "send_message", thread_id: tid, message: text }),
      });

      if (!resp.ok || !resp.body) {
        throw new Error("Stream not available (status " + resp.status + ")");
      }

      // Seed an assistant message that we'll progressively fill
      let assistantIdx = -1;
      setCoreMessages(p => {
        assistantIdx = p.length;
        return [...p, { role: "core", text: "" }];
      });

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let assistantText = "";
      let streamDone = false;

      while (!streamDone) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // Parse complete SSE events out of the buffer
        let idx;
        while ((idx = buffer.indexOf("\n\n")) !== -1) {
          const chunk = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          const lines = chunk.split("\n");
          let eventType = null;
          let dataLine = null;
          for (const line of lines) {
            if (line.startsWith("event:")) eventType = line.slice(6).trim();
            else if (line.startsWith("data:")) dataLine = line.slice(5).trim();
          }
          if (!eventType || dataLine === null) continue;

          let payload;
          try { payload = JSON.parse(dataLine); } catch { continue; }

          if (eventType === "text") {
            assistantText += payload.delta || "";
            setCoreMessages(p => p.map((m, i) => i === assistantIdx ? { ...m, text: assistantText } : m));
          } else if (eventType === "tool_call") {
            setToolActivity("Running " + payload.name + "...");
          } else if (eventType === "tool_result") {
            setToolActivity(null);
          } else if (eventType === "pending_action") {
            setPendingAction(payload);
          } else if (eventType === "done") {
            setToolActivity(null);
            streamDone = true;
            if (assistantText) speak(assistantText.length > 300 ? assistantText.slice(0, 300) : assistantText);
          } else if (eventType === "error") {
            setOrbError(true);
            setTimeout(() => setOrbError(false), 3000);
            const errMsg = "_Error: " + (payload.message || "Unknown error") + "_";
            assistantText = (assistantText ? assistantText + "\n\n" : "") + errMsg;
            setCoreMessages(p => p.map((m, i) => i === assistantIdx ? { ...m, text: assistantText } : m));
            streamDone = true;
          }
        }
      }
    } catch (e) {
      console.warn("[MC] sendCoreMsg failed:", e.message);
      setOrbError(true);
      setTimeout(() => setOrbError(false), 3000);
      if (!navigator.onLine) {
        setApiStatus("offline");
        setCoreMessages(p => [...p, { role: "core", text: "You're offline. I can still handle local commands — try opening tools, looking up contacts, or checking your pipeline." }]);
      } else {
        setApiStatus("error");
        setCoreMessages(p => [...p, { role: "core", text: "Connection issue — couldn't reach Core. Try again in a moment." }]);
        setTimeout(() => setApiStatus("online"), 5000);
      }
      setToolActivity(null);
    }
    setCoreLoading(false);
  }, [threadId, speak]);
  sendCoreMsgRef.current = sendCoreMsg;

  // ── Voice Command Processing (defined second — uses sendCoreMsg) ──
  const callRecs = useMemo(() => { const r = []; contacts.filter(c=>c.score>=80&&c.ph).sort((a,b)=>b.score-a.score).slice(0,4).forEach(c=>r.push({contact:c,reason:"Score "+c.score})); staleDeals.slice(0,2).forEach(d=>{const ct=contacts.find(c=>c.id===d.ct);if(ct&&ct.ph&&!r.find(x=>x.contact.id===ct.id))r.push({contact:ct,reason:d.days+"d stale"});}); return r.slice(0,5); }, [contacts, staleDeals]);

  const processVoiceCommandRef = useRef(null);
  const processVoiceCommand = useCallback((text, skipAdd) => {
    if (!skipAdd) setCoreMessages(p => [...p, { role: "user", text }]);

    const result = processCommand(text, {
      store, contacts, deals, tasks, activities, callQueue, kpiTargets,
      currentUserMember, pipe, weighted, won, staleDeals, hot,
      // CoreCommands ctx still receives keys named todayDials/todayEmails
      // (the voice agent prompt references them by those names) but the
      // values are now per-user, deduped, tz-aware counts.
      todayDials: todayCallsMade, todayEmails: todayEmailsSent, overdue, todayTasks,
      teamMembers: store.teamMembers || [],
    });

    // Execute result
    if (result.multiMatch && result.multiMatch.length > 1) {
      setDisambig({ contacts: result.multiMatch, intentId: result.intentId, rawText: result.rawText });
      const names = result.multiMatch.slice(0, 4).map(c => c.fn + " " + c.ln).join(", ");
      setCoreMessages(p => [...p, { role: "core", text: `I found ${result.multiMatch.length} matches: ${names}. Which one?` }]);
      speak(`I found ${result.multiMatch.length} people matching that name. Which one did you mean?`);
      setCoreLoading(false);
      return;
    }
    if (result.modal) setModal(result.modal);
    if (result.edge) setEdgePanel(result.edge);
    if (result.briefing) setShowBriefing(true);
    if (result.closeAll) { setEdgePanel(null); setModal(null); setShowBriefing(false); }
    if (result.contact) setSelectedContact(result.contact);
    if (result.deal) setSelectedDealId(result.deal.id);
    if (result.callTarget) {
      setCallTarget(result.callTarget);
      if (result.callTarget.ph || result.callTarget.phone) {
        window.__coreflow_auto_dial = { phone: result.callTarget.ph || result.callTarget.phone, contact: result.callTarget };
      }
      // Prepend to front of queue so dialer shows this contact first
      const ct = result.callTarget;
      store.setCallQueue(prev => [{ ...ct, ct: ct.id, fn: ct.fn, ln: ct.ln, ph: ct.ph, co: ct.co, title: ct.title, attempts: 0, id: ct.id }, ...prev.filter(q => q.ct !== ct.id)]);
      setModal("dialer");
    }
    if (result.queueContact && !result.callTarget) store.addToQueue(result.queueContact);
    if (result.createTask) store.addTask({ ...result.createTask, due: result.createTask.due || new Date().toISOString().slice(0, 10), pri: result.createTask.pri || "medium" });
    if (result.refresh) window.location.reload();
    if (result.type === "navigate" && result.page) setPage(result.page);

    if (result.speech) {
      setCoreMessages(p => [...p, { role: "core", text: result.speech }]);
      speak(result.speech);
    }

    if (result.type === "ai") {
      sendCoreMsgRef.current?.(text, result.aiPrompt);
    }
  }, [store, contacts, deals, tasks, activities, callQueue, kpiTargets, currentUserMember, pipe, weighted, won, staleDeals, hot, todayCallsMade, todayEmailsSent, overdue, todayTasks, speak]);
  processVoiceCommandRef.current = processVoiceCommand;

  // ── Spacebar Push-to-Talk (defined last — uses processVoiceCommand) ──
  const startListening = useCallback(async () => {
    if (!SpeechRecognition) {
      setCoreMessages(p => [...p, { role: "core", text: "Voice input isn't supported in this browser. Try Chrome or Edge, or type your command below." }]);
      speak("Voice isn't supported in this browser. You can type your command instead.");
      return;
    }
    if (coreListening) return;
    if (apiStatus === "offline") {
      setCoreMessages(p => [...p, { role: "core", text: "You're offline. Local commands still work, but AI features need a connection." }]);
    }
    if (micPermission === "denied") {
      setCoreMessages(p => [...p, { role: "core", text: "Microphone access was denied. Enable it in your browser settings, or type your command below." }]);
      speak("Microphone is blocked. You can type commands instead.");
      return;
    }
    if (micPermission !== "granted" && micPermission !== "unknown") {
      const ok = await requestMicPermission();
      if (!ok) {
        setCoreMessages(p => [...p, { role: "core", text: "I need microphone access for voice commands. You can also type below." }]);
        return;
      }
    }
    try {
      const recognition = new SpeechRecognition();
      recognition.continuous = false; recognition.interimResults = false; recognition.lang = "en-US";
      recognition.onresult = (e) => {
        const text = e.results[0][0].transcript;
        setCoreListening(false);
        processVoiceCommandRef.current?.(text);
      };
      recognition.onerror = (e) => {
        setCoreListening(false);
        if (e.error === "not-allowed") {
          setMicPermission("denied");
          setCoreMessages(p => [...p, { role: "core", text: "Microphone access denied. Type commands below instead." }]);
        } else if (e.error === "no-speech") {
          // Silent
        } else if (e.error === "network") {
          setOrbError(true);
          setCoreMessages(p => [...p, { role: "core", text: "Speech recognition needs an internet connection." }]);
          setTimeout(() => setOrbError(false), 3000);
        }
      };
      recognition.onend = () => { setCoreListening(false); };
      recognitionRef.current = recognition;
      recognition.start();
      setCoreListening(true);
    } catch (e) {
      setCoreListening(false);
      setOrbError(true);
      setTimeout(() => setOrbError(false), 3000);
      setCoreMessages(p => [...p, { role: "core", text: "Couldn't start voice input. Try typing instead." }]);
    }
  }, [coreListening, micPermission, apiStatus, speak]);

  const stopListening = useCallback(() => {
    recognitionRef.current?.stop();
    setCoreListening(false);
  }, []);

  useEffect(() => {
    const down = (e) => {
      if (e.code === "Space" && !e.repeat && !spaceHeldRef.current) {
        const tag = document.activeElement?.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
        e.preventDefault();
        spaceHeldRef.current = true;
        startListening();
      }
    };
    const up = (e) => {
      if (e.code === "Space" && spaceHeldRef.current) {
        e.preventDefault();
        spaceHeldRef.current = false;
        stopListening();
      }
    };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => { window.removeEventListener("keydown", down); window.removeEventListener("keyup", up); };
  }, [startListening, stopListening]);

  const sendCore = useCallback(() => { if(!coreInput.trim()) return; const m=coreInput.trim(); setCoreInput(""); setCoreMessages(p=>[...p,{role:"user",text:m}]); processVoiceCommand(m, true); }, [coreInput, processVoiceCommand]);
  useEffect(() => { if(coreMessages.length) chatRef.current?.scrollIntoView({behavior:"smooth"}); }, [coreMessages]);

  const completeTask = (id) => store.setTasks(prev => prev.map(t => t.id===id?{...t,done:true}:t));

  // Contact/Deal handlers for MC context
  const handleMCContact = (c) => { if (typeof c === "object") setSelectedContact(c); else { const ct = contacts.find(x=>x.id===c); if(ct) setSelectedContact(ct); } };
  const handleMCDeal = (d) => { setSelectedDealId(typeof d === "object" ? d.id : d); };

  const ALL_ACTIONS = [
    { id:"kpi",label:"KPIs",icon:"pieChart",type:"edge" },{ id:"priority",label:"Priority",icon:"target",type:"edge" },{ id:"tasks",label:"Tasks",icon:"check",type:"edge" },{ id:"activity",label:"Activity",icon:"clock",type:"edge" },{ id:"agenda",label:"Agenda",icon:"calendar",type:"edge" },{ id:"coreHistory",label:"Core",icon:"ai",type:"edge" },
    { id:"dialer",label:"Dialer",icon:"phoneFilled",type:"system" },{ id:"callLogs",label:"Calls",icon:"phone",type:"system" },{ id:"email",label:"Email",icon:"mail",type:"system" },{ id:"sms",label:"SMS",icon:"chat",type:"system" },
    { id:"calendar",label:"Calendar",icon:"calendar",type:"system" },{ id:"teamChat",label:"Chat",icon:"chat",type:"system" },{ id:"projects",label:"Projects",icon:"folder",type:"system" },{ id:"workflows",label:"Workflows",icon:"workflow",type:"system" },
  ];
  const DEFAULT_VISIBLE = ["kpi","priority","tasks","activity","agenda","dialer","callLogs","email","sms","calendar","teamChat","projects","workflows"];
  const [visibleActions, setVisibleActions] = useState(() => {
    try { const saved = localStorage.getItem("cf_mc_actions"); return saved ? JSON.parse(saved) : DEFAULT_VISIBLE; } catch { return DEFAULT_VISIBLE; }
  });
  const [showActionConfig, setShowActionConfig] = useState(false);
  const [barWidth, setBarWidth] = useState(() => {
    try { return parseInt(localStorage.getItem("cf_mc_bar_width")) || 76; } catch { return 76; }
  });
  const toggleAction = (id) => {
    setVisibleActions(prev => {
      const next = prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id];
      localStorage.setItem("cf_mc_actions", JSON.stringify(next));
      return next;
    });
  };
  const iconRow = ALL_ACTIONS.filter(a => visibleActions.includes(a.id));
  // Insert separators between edge and system groups
  const edgeItems = iconRow.filter(a => a.type === "edge");
  const systemItems = iconRow.filter(a => a.type === "system");
  const iconRowWithSeps = [...edgeItems, ...(edgeItems.length > 0 && systemItems.length > 0 ? [{ id:"sep1" }] : []), ...systemItems];

  // Orb state: idle, listening, speaking, thinking
  const orbState = orbError ? "error" : coreListening ? "listening" : coreSpeaking ? "speaking" : coreLoading ? "thinking" : "idle";

  return (
    <div className="cf-no-anim" style={{ height:"calc(100vh - 52px)", position:"relative", overflow:"hidden", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center" }}>
      <style>{`
        @keyframes imFade{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
        @keyframes imPulse{0%,100%{opacity:0.3}50%{opacity:1}}
        @keyframes imVoice{0%,100%{height:3px}50%{height:18px}}
        @keyframes imGlow{0%,100%{opacity:0.3;transform:scale(1)}50%{opacity:0.7;transform:scale(1.05)}}
        @keyframes imListenGlow{0%,100%{opacity:0.5;transform:scale(1)}50%{opacity:0.9;transform:scale(1.08)}}
        @keyframes imListenRing{0%{transform:scale(1);opacity:0.4}100%{transform:scale(1.6);opacity:0}}
        .im-icon{transition:all .2s cubic-bezier(.32,1,.23,1)}.im-icon:hover{transform:translateY(-2px);background:rgba(255,255,255,0.1)}
        input::placeholder{color:rgba(250,250,250,0.45)!important}
      `}</style>

      {/* ORB + GLOW */}
      <div style={{ position:"absolute", inset:0, right: isMobile ? 0 : 90, display:"flex", alignItems:"center", justifyContent:"center" }}>
        {/* Glow aura — changes by state */}
        <div style={{
          position:"absolute",
          width: orbState==="error"?280:orbState==="listening"?360:orbState==="speaking"?380:orbState==="thinking"?320:0,
          height: orbState==="error"?280:orbState==="listening"?360:orbState==="speaking"?380:orbState==="thinking"?320:0,
          borderRadius:"50%",
          background: orbState==="error"
            ? `radial-gradient(circle, "#E53935"20 0%, "#E53935"10 40%, transparent 70%)`
            : orbState==="listening"
            ? `radial-gradient(circle, ${A}25 0%, ${AL}12 40%, transparent 70%)`
            : orbState==="speaking"
            ? `radial-gradient(circle, ${A}30 0%, ${AL}15 40%, transparent 70%)`
            : orbState==="thinking"
            ? `radial-gradient(circle, #7C5CFC20 0%, #7C5CFC10 40%, transparent 70%)`
            : "none",
          filter: `blur(${orbState==="listening"?30:orbState==="speaking"?35:20}px)`,
          transition:"all .6s cubic-bezier(.32,1,.23,1)",
          animation: orbState==="listening"?"imListenGlow 1s ease infinite":orbState==="speaking"?"imGlow 1.5s ease infinite":"none",
        }} />
        {/* Listening pulse ring */}
        {coreListening && <div style={{ position:"absolute", width: isMobile ? 180 : 300, height: isMobile ? 180 : 300, borderRadius:"50%", border:`2px solid ${A}30`, animation:"imListenRing 1.5s ease-out infinite" }} />}
        {coreListening && <div style={{ position:"absolute", width: isMobile ? 180 : 300, height: isMobile ? 180 : 300, borderRadius:"50%", border:`2px solid ${A}20`, animation:"imListenRing 1.5s ease-out 0.5s infinite" }} />}
        <CoreOrb size={isMobile ? 200 : 330} speaking={coreSpeaking} listening={coreListening} thinking={coreLoading} error={orbError} />
      </div>

      {/* Greeting */}
      <div style={{ position:"absolute", top: isMobile ? 12 : 20, left: isMobile ? 12 : 20 }}>
        <div style={{ fontSize: isMobile ? 20 : 26, fontWeight:300, color:T.txt, marginLeft:-2 }}>{greeting}, <span className="cf-bold" style={{ color:"#4059AD" }}>{firstName}</span></div>
        <div style={{ fontSize: isMobile ? 11 : 13, color:T.tx3, marginTop:4, letterSpacing:"0.01em" }}>{new Date().toLocaleDateString("en-US",{weekday:"long",month:"long",day:"numeric"})}</div>
      </div>

      {/* Status - only show when actively listening/speaking/thinking/error */}
      {/* Status label removed — orb animation is the indicator */}

      {/* Offline Banner */}
      {apiStatus!=="online" && <div style={{ position:"absolute", top:70, left:"50%", transform:"translateX(-50%)", display:"flex", alignItems:"center", gap:8, padding:"8px 18px", borderRadius: 8, background:apiStatus==="offline"?"rgba(239,68,68,0.08)":"rgba(245,158,11,0.08)", border:`1px solid ${apiStatus==="offline"?"rgba(239,68,68,0.2)":"rgba(245,158,11,0.2)"}`, backdropFilter:"blur(20px)", animation:"imFade .3s ease", zIndex:100 }}>
        <div style={{ width:6, height:6, borderRadius:3, background:apiStatus==="offline"?"#E53935":"#D4763A" }} />
        <span style={{ fontSize:11, color:apiStatus==="offline"?"#E53935":"#D4763A", fontWeight:400 }}>{apiStatus==="offline"?"You're offline — AI features unavailable":"Connection unstable — retrying"}</span>
      </div>}

      {/* Chat Bar + Inline Messages */}
      <div style={{ position:"absolute", bottom: isMobile ? "calc(20px + env(safe-area-inset-bottom, 0px))" : 20, left:0, right: isMobile ? 0 : 90, display:"flex", flexDirection:"column", alignItems:"center" }}>
        {/* Inline message thread — scrolls above the input */}
        {coreMessages.length > 0 && (
          <div style={{ width:560, maxWidth:"90%", maxHeight:320, overflowY:"auto", marginBottom:8, display:"flex", flexDirection:"column", gap:6, borderRadius:8, background:"rgba(44,46,51,0.95)", border:"1px solid rgba(217,219,221,0.08)", backdropFilter:"blur(12px)", position:"relative" }}>
            <div style={{ display:"flex", justifyContent:"flex-end", gap:6, padding:"8px 12px 4px", position:"sticky", top:0, background:"rgba(44,46,51,0.98)", zIndex:2, borderRadius:"8px 8px 0 0" }}>
              <button onClick={() => { setCoreMessages([]); sessionStorage.removeItem("cf_chat_session"); }} style={{ padding:"3px 8px", borderRadius:4, border:"1px solid rgba(217,219,221,0.08)", background:"transparent", color:"rgba(217,219,221,0.4)", fontSize:9, cursor:"pointer", fontFamily:"inherit" }}>New Chat</button>
              <button onClick={() => setCoreMessages([])} style={{ width:20, height:20, borderRadius:4, border:"1px solid rgba(217,219,221,0.08)", background:"transparent", color:"rgba(217,219,221,0.4)", fontSize:11, cursor:"pointer", fontFamily:"inherit", display:"flex", alignItems:"center", justifyContent:"center", lineHeight:1 }}>×</button>
            </div>
            <div style={{ padding:"0 16px 12px", display:"flex", flexDirection:"column", gap:6 }}>
            {coreMessages.map((msg,i)=>(<div key={i} style={{ display:"flex", justifyContent:msg.role==="user"?"flex-end":"flex-start" }}><div style={{ maxWidth:"85%", padding: msg.role==="user" ? "8px 12px" : "12px 16px", fontSize:12, lineHeight:1.6, color: msg.role==="user" ? "#FAFAFA" : "rgba(217,219,221,0.7)", background:msg.role==="user"?"#4059AD":"rgba(255,255,255,0.04)", border:msg.role==="user"?"none":"1px solid rgba(217,219,221,0.08)", borderRadius:8 }}>{msg.role==="user" ? msg.text : renderCoreMessage(msg.text)}</div></div>))}
            {coreLoading&&<div style={{ display:"flex", alignItems:"center", gap:8, padding:"10px 14px", borderRadius:8, background:"rgba(255,255,255,0.03)", border:"1px solid rgba(217,219,221,0.06)" }}>
              <div style={{ display:"flex", gap:3, alignItems:"center" }}>{[0,1,2].map(i=><div key={i} style={{ width:5, height:5, borderRadius:3, background:"#FAFAFA", animation:`coreThink 1.2s ease ${i*.2}s infinite` }} />)}</div>
              <span style={{ fontSize:11, color:"rgba(217,219,221,0.4)" }}>{toolActivity || "Core is thinking..."}</span>
              <style>{`@keyframes coreThink{0%,80%,100%{opacity:0.15;transform:scale(0.8)}40%{opacity:1;transform:scale(1.1)}}`}</style>
            </div>}
            <div ref={chatRef} />
            </div>
          </div>
        )}
        <div style={{ width:560, maxWidth:"90%", display:"flex", gap:8, alignItems:"center" }}>
        <button onClick={toggleVoice} title={voiceEnabled ? "Voice on — click to mute" : "Voice off — click to enable"} style={{ width:42, height:42, borderRadius:8, border:"1px solid rgba(217,219,221,0.12)", background:voiceEnabled?"rgba(64,89,173,0.15)":"rgba(255,255,255,0.05)", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, transition:"all .2s" }}>
          {voiceEnabled ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#FAFAFA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/></svg>
          : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(250,250,250,0.4)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/></svg>}
        </button>
        <input value={coreInput} onChange={e=>setCoreInput(e.target.value)} onKeyDown={e=>{if(e.key==="Enter")sendCore();}} placeholder="Ask Core anything... or hold Space to talk" style={{ flex:1, padding:"13px 18px", borderRadius: 8, border:"1px solid rgba(217,219,221,0.12)", background:"rgba(255,255,255,0.05)", fontSize:13, color:"#FAFAFA", outline:"none", fontFamily:"inherit", boxShadow:"none", transition:"all .2s" }} />
        <button onClick={sendCore} style={{ padding:"13px 22px", borderRadius: 8, border:"1px solid rgba(217,219,221,0.12)", background:"rgba(255,255,255,0.06)", color:"#FAFAFA", fontSize:12, fontWeight:400, cursor:"pointer", fontFamily:"inherit", boxShadow:"none", transition:"all .2s" }}>Send</button>
        </div>
      </div>

      {/* Vertical Icon Bar — Right of Orb */}
      {!isMobile && (() => {
        const minW = 64, maxW = 160;
        const iconSize = Math.max(13, Math.min(22, Math.round((barWidth - 40) * 0.4)));
        const labelSize = Math.max(6, Math.min(11, Math.round((barWidth - 40) * 0.18)));
        const iconGap = barWidth > 100 ? 2 : 0;
        return (
        <div style={{ position:"absolute", right:16, top:10, bottom:70, display:"flex", zIndex:10 }}>
          {/* Drag handle */}
          <div onMouseDown={(e) => {
            e.preventDefault();
            const startX = e.clientX;
            const startW = barWidth;
            const onMove = (ev) => {
              const newW = Math.max(minW, Math.min(maxW, startW + (startX - ev.clientX)));
              setBarWidth(newW);
              document.body.dataset.mcBarW = newW;
            };
            const onUp = () => {
              document.removeEventListener("mousemove", onMove);
              document.removeEventListener("mouseup", onUp);
              localStorage.setItem("cf_mc_bar_width", document.body.dataset.mcBarW || String(startW));
            };
            document.addEventListener("mousemove", onMove);
            document.addEventListener("mouseup", onUp);
          }} style={{ width:6, cursor:"ew-resize", display:"flex", alignItems:"center", justifyContent:"center", marginRight:0 }}>
            <div style={{ width:2, height:32, borderRadius:1, background:"rgba(250,250,250,0.15)" }} />
          </div>
          <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:0, padding:"8px 6px", borderRadius: 8, background:"#4059AD", border:"1px solid rgba(64,89,173,0.4)", width:barWidth, overflowY:"auto", overflowX:"hidden", flex:1, minHeight:0, justifyContent:"space-between" }}>
            {iconRowWithSeps.map((item,i) => item.id.startsWith("sep") ? (<div key={i} style={{ width:"60%", height:1, background:"rgba(250,250,250,0.20)", flexShrink:0 }} />) : (
              <button key={item.id} onClick={()=>item.type==="edge"?setEdgePanel(item.id):setModal(item.id)} className="im-icon" style={{ padding:"4px 4px", borderRadius:8, border:"none", background:"transparent", cursor:"pointer", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:iconGap, fontFamily:"inherit", width:"100%", flexShrink:0 }}><Icon name={item.icon} size={iconSize} color="#FAFAFA" /><span style={{ fontSize:labelSize, fontWeight:400, color:"#FAFAFA", letterSpacing:".02em", whiteSpace:"nowrap" }}>{item.label}</span></button>
            ))}
            <div style={{ width:"60%", height:1, background:"rgba(250,250,250,0.20)", flexShrink:0 }} />
            <button onClick={()=>setShowActionConfig(!showActionConfig)} className="im-icon" style={{ padding:"4px 4px", borderRadius:8, border:"none", background:showActionConfig?"rgba(255,255,255,0.15)":"transparent", cursor:"pointer", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:0, fontFamily:"inherit", width:"100%", flexShrink:0 }}><Icon name="settings" size={Math.max(10, iconSize - 3)} color="rgba(250,250,250,0.6)" /><span style={{ fontSize:Math.max(5, labelSize - 1), fontWeight:400, color:"rgba(250,250,250,0.5)", letterSpacing:".02em" }}>Edit</span></button>
          </div>
        </div>
        );
      })()}

      {/* Quick Actions Config Panel */}
      {showActionConfig && <CenterModal title="Customize Quick Actions" onClose={()=>setShowActionConfig(false)} width={380}>
        <div style={{ padding:"12px 20px" }}>
          <div style={{ fontSize:11, fontWeight:300, color:T.tx3, marginBottom:12 }}>Toggle which buttons appear in your sidebar.</div>
          {ALL_ACTIONS.map(a => (
            <div key={a.id} style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 0", borderBottom:"1px solid rgba(217,219,221,0.06)" }}>
              <button onClick={()=>toggleAction(a.id)} style={{
                width:18, height:18, borderRadius:4, flexShrink:0,
                border:"1.5px solid "+(visibleActions.includes(a.id)?"#4059AD":"rgba(217,219,221,0.25)"),
                background:visibleActions.includes(a.id)?"#4059AD":"transparent",
                cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center",
              }}>
                {visibleActions.includes(a.id) && <svg width="10" height="10" viewBox="0 0 12 12"><path d="M2 6l3 3 5-5" stroke="#fff" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg>}
              </button>
              <Icon name={a.icon} size={14} color="#FAFAFA" />
              <span style={{ fontSize:12, fontWeight:400, color:"#FAFAFA", flex:1 }}>{a.label}</span>
              <span style={{ fontSize:9, fontWeight:300, color:T.tx3 }}>{a.type==="edge"?"Panel":"Page"}</span>
            </div>
          ))}
          <button onClick={()=>{setVisibleActions(DEFAULT_VISIBLE);localStorage.setItem("cf_mc_actions",JSON.stringify(DEFAULT_VISIBLE));}} style={{ marginTop:12, width:"100%", padding:"8px 0", borderRadius:8, border:"1px solid rgba(217,219,221,0.12)", background:"transparent", color:"#FAFAFA", fontSize:11, fontWeight:400, cursor:"pointer", fontFamily:"inherit" }}>Reset to Default</button>
        </div>
      </CenterModal>}

      {/* ═══ DAILY BRIEFING ═══ */}
      <EdgePanel side="top" visible={showBriefing} onClose={()=>setShowBriefing(false)} title="Daily Briefing">
        {briefingData && (<div style={{ display:"flex", gap:20, flexWrap:"wrap" }}>
          <div style={{ flex:1, minWidth:160 }}><div style={{ fontSize:10, fontWeight:300, color:T.tx3, letterSpacing:".1em", textTransform:"uppercase" }}>Pipeline</div><div style={{ fontSize:28, fontWeight:400, color:T.txt, marginTop:4 }}>{briefingData.pipeline}</div><div style={{ fontSize:11, color:T.tx3 }}>{briefingData.pipeDeals} active deals</div></div>
          <div style={{ flex:1, minWidth:160 }}><div style={{ fontSize:10, fontWeight:300, color:T.tx3, letterSpacing:".1em", textTransform:"uppercase" }}>Forecast</div><div style={{ fontSize:28, fontWeight:400, color:T.txt, marginTop:4 }}>{briefingData.forecast}</div><div style={{ fontSize:11, color:T.tx3 }}>Won: <span style={{ color:"#2E8B57", fontWeight:400 }}>{briefingData.won}</span></div></div>
          <div style={{ flex:1, minWidth:140 }}><div style={{ fontSize:10, fontWeight:300, color:T.tx3, letterSpacing:".1em", textTransform:"uppercase" }}>Today</div><div style={{ display:"flex", gap:12, marginTop:8 }}><Ring pct={dialPct} size={44} stroke={3} color={A}><div style={{textAlign:"center"}}><div style={{fontSize:13,fontWeight:400,color:T.txt}}>{briefingData.dials}</div><div style={{fontSize:6,color:T.tx3,fontWeight:400}}>DIALS</div></div></Ring><Ring pct={emailPct} size={44} stroke={3} color={"#6B5B95"}><div style={{textAlign:"center"}}><div style={{fontSize:13,fontWeight:400,color:T.txt}}>{briefingData.emails}</div><div style={{fontSize:6,color:T.tx3,fontWeight:400}}>EMAIL</div></div></Ring></div></div>
          <div style={{ flex:1, minWidth:160 }}><div style={{ fontSize:10, fontWeight:300, color:T.tx3, letterSpacing:".1em", textTransform:"uppercase" }}>Attention</div><div style={{ marginTop:6 }}>{briefingData.overdue>0&&<div style={{ fontSize:12, color:"#E53935", fontWeight:400 }}>{briefingData.overdue} overdue tasks</div>}{briefingData.tasksDue>0&&<div style={{ fontSize:12, color:A, fontWeight:400 }}>{briefingData.tasksDue} tasks due today</div>}{briefingData.staleCount>0&&<div style={{ fontSize:12, color:"#D4763A", fontWeight:400 }}>{briefingData.staleCount} stale deals</div>}{briefingData.hotLeads.length>0&&<div style={{ fontSize:11, color:T.tx3, marginTop:4 }}>Hot: {briefingData.hotLeads.join(", ")}</div>}</div></div>
        </div>)}
      </EdgePanel>

      {/* ═══ EDGE PANELS ═══ */}
      <EdgePanel side="top" visible={edgePanel==="kpi"} onClose={()=>setEdgePanel(null)} title="Key Metrics">
        <div style={{ display:"flex", gap:16, flexWrap:"wrap" }}>
          {/* Common: calls + emails */}
          <div style={{ flex:1, minWidth:120 }}><div style={{ fontSize:10, fontWeight:300, color:T.tx3, letterSpacing:".1em", textTransform:"uppercase" }}>Calls Made</div><div style={{ fontSize:28, fontWeight:400, color:"#FAFAFA", marginTop:4 }}>{todayCallsMade}{!isManager && targets.dials > 0 ? <span style={{ fontSize:14, fontWeight:300, color:T.tx3 }}>/{targets.dials}</span> : null}</div></div>
          <div style={{ flex:1, minWidth:120 }}><div style={{ fontSize:10, fontWeight:300, color:T.tx3, letterSpacing:".1em", textTransform:"uppercase" }}>Calls Received</div><div style={{ fontSize:28, fontWeight:400, color:"#FAFAFA", marginTop:4 }}>{todayCallsReceived}</div></div>
          <div style={{ flex:1, minWidth:120 }}><div style={{ fontSize:10, fontWeight:300, color:T.tx3, letterSpacing:".1em", textTransform:"uppercase" }}>Emails Sent</div><div style={{ fontSize:28, fontWeight:400, color:"#FAFAFA", marginTop:4 }}>{todayEmailsSent}{!isManager && targets.emails > 0 ? <span style={{ fontSize:14, fontWeight:300, color:T.tx3 }}>/{targets.emails}</span> : null}</div></div>
          <div style={{ flex:1, minWidth:120 }}><div style={{ fontSize:10, fontWeight:300, color:T.tx3, letterSpacing:".1em", textTransform:"uppercase" }}>Tasks Due</div><div style={{ fontSize:28, fontWeight:400, color:"#FAFAFA", marginTop:4 }}>{todayTasks.length + overdue.length}</div><div style={{ fontSize:11, fontWeight:300, color:T.tx3 }}>{overdue.length} overdue</div></div>
          {/* Manager/Admin specific */}
          {isManager && <>
            <div style={{ flex:1, minWidth:140 }}><div style={{ fontSize:10, fontWeight:300, color:T.tx3, letterSpacing:".1em", textTransform:"uppercase" }}>Pipeline</div><div style={{ fontSize:28, fontWeight:400, color:"#FAFAFA", marginTop:4 }}>{fmtDollar(pipe)}</div><div style={{ fontSize:11, fontWeight:300, color:T.tx3 }}>{activeDeals.length} deals</div></div>
            <div style={{ flex:1, minWidth:140 }}><div style={{ fontSize:10, fontWeight:300, color:T.tx3, letterSpacing:".1em", textTransform:"uppercase" }}>Revenue Won</div><div style={{ fontSize:28, fontWeight:400, color:"#FAFAFA", marginTop:4 }}>{fmtDollar(won)}</div></div>
            <div style={{ flex:1, minWidth:120 }}><div style={{ fontSize:10, fontWeight:300, color:T.tx3, letterSpacing:".1em", textTransform:"uppercase" }}>Meetings Today</div><div style={{ fontSize:28, fontWeight:400, color:"#FAFAFA", marginTop:4 }}>{todayEvents.filter(e => e.status !== "cancelled").length}</div></div>
            <div style={{ flex:1, minWidth:140 }}><div style={{ fontSize:10, fontWeight:300, color:T.tx3, letterSpacing:".1em", textTransform:"uppercase" }}>Team Activity</div><div style={{ fontSize:28, fontWeight:400, color:"#FAFAFA", marginTop:4 }}>{orgCallsMade + orgCallsReceived + orgEmailsSent}</div><div style={{ fontSize:11, fontWeight:300, color:T.tx3 }}>{orgCallsMade} made · {orgCallsReceived} received · {orgEmailsSent} emails</div></div>
          </>}
          {/* SDR specific */}
          {!isManager && <>
            <div style={{ flex:1, minWidth:120 }}><div style={{ fontSize:10, fontWeight:300, color:T.tx3, letterSpacing:".1em", textTransform:"uppercase" }}>Connect Rate</div><div style={{ fontSize:28, fontWeight:400, color:"#FAFAFA", marginTop:4 }}>{connectRate}%</div></div>
            <div style={{ flex:1, minWidth:120 }}><div style={{ fontSize:10, fontWeight:300, color:T.tx3, letterSpacing:".1em", textTransform:"uppercase" }}>Queue</div><div style={{ fontSize:28, fontWeight:400, color:"#FAFAFA", marginTop:4 }}>{queueSize}</div></div>
            <div style={{ flex:1, minWidth:120 }}><div style={{ fontSize:10, fontWeight:300, color:T.tx3, letterSpacing:".1em", textTransform:"uppercase" }}>Meetings Booked</div><div style={{ fontSize:28, fontWeight:400, color:"#FAFAFA", marginTop:4 }}>{todayEvents.filter(e => e.status !== "cancelled").length}</div></div>
            {targets.linkedin_dms > 0 && <div style={{ flex:1, minWidth:120 }}><div style={{ fontSize:10, fontWeight:300, color:T.tx3, letterSpacing:".1em", textTransform:"uppercase" }}>DMs Today</div><div style={{ fontSize:28, fontWeight:400, color:"#FAFAFA", marginTop:4 }}>0<span style={{ fontSize:14, fontWeight:300, color:T.tx3 }}>/{targets.linkedin_dms}</span></div></div>}
          </>}
        </div>
      </EdgePanel>

      <EdgePanel side="left" visible={edgePanel==="priority"} onClose={()=>setEdgePanel(null)} title="Priority" width={320}>
        {callRecs.map((r,i)=>(<div key={i} onClick={()=>handleMCContact(r.contact)} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 10px",borderRadius: 8,cursor:"pointer",transition:"background .12s"}} onMouseEnter={e=>e.currentTarget.style.background="rgba(64,89,173,0.05)"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}><div style={{flex:1}}><div style={{fontSize:12,fontWeight:400,color:T.txt}}>{r.contact.fn} {r.contact.ln}</div><div style={{fontSize:10,color:T.tx3}}>{r.reason}</div></div><button onClick={e=>{e.stopPropagation();addToQueue(r.contact);setModal("dialer");}} onMouseEnter={e=>{e.currentTarget.style.background="#4059AD";e.currentTarget.style.color="#FAFAFA";e.currentTarget.style.borderColor="#4059AD";}} onMouseLeave={e=>{e.currentTarget.style.background="rgba(255,255,255,0.06)";e.currentTarget.style.color="#FAFAFA";e.currentTarget.style.borderColor="rgba(217,219,221,0.10)";}} style={{padding:"3px 10px",borderRadius:6,border:"1px solid rgba(217,219,221,0.10)",background:"rgba(255,255,255,0.06)",color:"#FAFAFA",fontSize:9,fontWeight:400,cursor:"pointer",fontFamily:"inherit",transition:"all .15s ease"}}>Call</button></div>))}
        {hot.length>0&&<><div style={{fontSize:10,fontWeight:400,color:T.tx3,letterSpacing:".1em",textTransform:"uppercase",marginTop:12,marginBottom:6}}>Hot Leads</div>{hot.map(c=>(<div key={c.id} onClick={()=>handleMCContact(c)} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 10px",borderRadius: 8,cursor:"pointer",transition:"background .12s"}} onMouseEnter={e=>e.currentTarget.style.background="rgba(64,89,173,0.05)"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}><div style={{flex:1}}><div style={{fontSize:12,fontWeight:400,color:T.txt}}>{c.fn} {c.ln}</div><div style={{fontSize:10,color:T.tx3}}>{c.co||""}</div></div><div style={{padding:"2px 7px",borderRadius:5,background:"rgba(255,255,255,0.06)",fontSize:10,fontWeight:400,color:"#FAFAFA"}}>{c.score}</div></div>))}</>}
      </EdgePanel>

      <EdgePanel side="right" visible={edgePanel==="tasks"} onClose={()=>setEdgePanel(null)} title={`Tasks (${overdue.length+todayTasks.length})`} width={340}>
        <button onClick={()=>setModal("newTask")} style={{width:"100%",padding:"8px 0",borderRadius: 8,border:"1px dashed rgba(217,219,221,0.20)",background:"transparent",color:"#FAFAFA",fontSize:11,fontWeight:400,cursor:"pointer",fontFamily:"inherit",marginBottom:10}}>+ New Task</button>
        {[...overdue,...todayTasks,...upcoming].slice(0,10).map(t=>(<div key={t.id} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 10px",borderRadius: 8,transition:"background .12s"}} onMouseEnter={e=>e.currentTarget.style.background="rgba(64,89,173,0.05)"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}><Checkbox checked={false} onChange={()=>completeTask(t.id)} /><div style={{flex:1}}><div style={{fontSize:12,fontWeight:400,color:T.txt}}>{t.title}</div><div style={{fontSize:10,color:t.due<today?"#E53935":t.due===today?A:T.tx3}}>{t.due<today?"Overdue":t.due===today?"Today":t.due}</div></div></div>))}
      </EdgePanel>

      <EdgePanel side="left" visible={edgePanel==="activity"} onClose={()=>setEdgePanel(null)} title="My Activity" width={360}>
        {/* Filter on currentUserId (UUID). Activity row's `by` is row.user_id
            mapped at dbActivityToLocal — a UUID — so this comparison actually
            matches. Then bucket by date in the user's local timezone before
            rendering as four collapsible sections. Activities older than the
            current calendar month land in "older" and are dropped — for
            history beyond that, the user opens the contact/deal record. */}
        {(() => {
          const userTz = currentUserMember?.timezone || "America/New_York";
          const userActivities = activities.filter(a => a.by && a.by === currentUserId);
          const buckets = { today: [], yesterday: [], thisWeek: [], thisMonth: [] };
          for (const act of userActivities) {
            const b = bucketActivity(act.created_at, userTz);
            if (b !== "older" && buckets[b]) buckets[b].push(act);
          }
          const sections = [
            { key: "today",     label: "Today" },
            { key: "yesterday", label: "Yesterday" },
            { key: "thisWeek",  label: "This Week" },
            { key: "thisMonth", label: "This Month" },
          ];
          return sections.map(({ key, label }) => {
            const items = buckets[key];
            // Hide empty non-today buckets entirely. Today always renders so
            // the user sees the empty-state copy when they have no activity.
            if (items.length === 0 && key !== "today") return null;
            const isExpanded = expandedBuckets[key];
            return (
              <div key={key} style={{ marginBottom: 12 }}>
                <div onClick={() => toggleBucket(key)} style={{ cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 4px", fontSize: 11, fontWeight: 400, color: T.tx3, letterSpacing: ".06em", textTransform: "uppercase", userSelect: "none" }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ display: "inline-block", transition: "transform 0.15s", transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)", fontSize: 9 }}>▸</span>
                    {label}
                  </span>
                  <span style={{ fontSize: 10, color: T.tx3 }}>{items.length}</span>
                </div>
                {isExpanded && (
                  <div>
                    {items.length === 0 ? (
                      <div style={{ padding: 20, textAlign: "center", color: T.tx3, fontSize: 12 }}>No activity yet today</div>
                    ) : (
                      items.map((act, i) => (
                        <div key={act.id || i} style={{ display:"flex", alignItems:"flex-start", gap:10, padding:"8px 0", borderBottom:"1px solid rgba(217,219,221,0.06)" }}>
                          <div style={{ width:6, height:6, borderRadius:3, background:act.type==="call"?"#4059AD":act.type==="email"?"#3D8B8B":act.type==="note"?"#FAFAFA":"#FAFAFA", flexShrink:0, marginTop:5 }} />
                          <div style={{ flex:1 }}>
                            <div style={{ fontSize:11, fontWeight:400, color:"#FAFAFA" }}>{act.title || act.type}</div>
                            {act.notes && <div style={{ fontSize:10, color:T.tx3, marginTop:2, lineHeight:1.4 }}>{(act.notes||"").slice(0,80)}{act.notes?.length>80?"...":""}</div>}
                            <div style={{ fontSize:9, fontWeight:300, color:T.tx3, marginTop:2 }}>{act.date || "—"}</div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            );
          });
        })()}
      </EdgePanel>

      <EdgePanel side="right" visible={edgePanel==="agenda"} onClose={()=>setEdgePanel(null)} title="Today's Agenda" width={360}>
        {todayEvents.length > 0 ? todayEvents.map(ev => {
          const st = new Date(ev.start_time);
          const et = new Date(ev.end_time);
          const fTime = (d) => { let h = d.getHours(); const ap = h >= 12 ? "PM" : "AM"; h = h % 12 || 12; return h + ":" + String(d.getMinutes()).padStart(2,"0") + " " + ap; };
          const isCancelled = ev.status === "cancelled";
          return (
            <div key={ev.id} style={{ display:"flex", alignItems:"flex-start", gap:10, padding:"10px 0", borderBottom:"1px solid rgba(217,219,221,0.06)", opacity: isCancelled ? 0.5 : 1 }}>
              <div style={{ width:4, height:32, borderRadius:2, background:ev.color||A, flexShrink:0, marginTop:2 }} />
              <div style={{ flex:1 }}>
                <div style={{ fontSize:12, fontWeight:400, color:"#FAFAFA", textDecoration: isCancelled ? "line-through" : "none" }}>{ev.title}</div>
                <div style={{ fontSize:10, fontWeight:300, color:T.tx3, marginTop:2 }}>{fTime(st)} – {fTime(et)}</div>
                {ev.location && <div style={{ fontSize:10, fontWeight:300, color:T.tx3 }}>{ev.location}</div>}
              </div>
            </div>
          );
        }) : <div style={{ padding:20, textAlign:"center", color:T.tx3, fontSize:12 }}>No meetings today</div>}
        {overdue.length > 0 && <>
          <div style={{ fontSize:10, fontWeight:300, color:T.tx3, letterSpacing:".1em", textTransform:"uppercase", marginTop:16, marginBottom:8 }}>Overdue Tasks</div>
          {overdue.slice(0,5).map(t => (
            <div key={t.id} style={{ display:"flex", alignItems:"center", gap:8, padding:"6px 0", borderBottom:"1px solid rgba(217,219,221,0.06)" }}>
              <Checkbox checked={false} onChange={()=>completeTask(t.id)} />
              <div style={{ flex:1 }}>
                <div style={{ fontSize:11, fontWeight:400, color:"#FAFAFA" }}>{t.title}</div>
                <div style={{ fontSize:9, fontWeight:300, color:T.tx3 }}>Due: {t.due}</div>
              </div>
            </div>
          ))}
        </>}
      </EdgePanel>

      {/* Core Chat History */}
      <EdgePanel side="right" visible={edgePanel==="coreHistory"} onClose={()=>setEdgePanel(null)} title="Core History" width={380}>
        <CoreHistoryPanel coreMessages={coreMessages} auth={auth} supabase={supabase} renderCoreMessage={renderCoreMessage} setCoreMessages={setCoreMessages} />
      </EdgePanel>

      {/* ═══ SYSTEM MODALS ═══ */}
      {modal==="dialer"&&<div data-modal="dialer"><SystemModal title="Power Dialer" onClose={()=>{setModal(null);setCallTarget(null);}} size="large">
        <div style={{ display:"flex", gap:0, height:"100%", overflow:"hidden" }}>
          {callTarget && <div style={{ width:260, flexShrink:0, borderRight:"1px solid rgba(0,0,0,0.20)", padding:"16px 18px", overflowY:"auto", background:"rgba(255,255,255,0.04)" }}>
            <div style={{ textAlign:"center", marginBottom:14 }}>
              
              <div style={{ fontSize:14, fontWeight:400, color:T.txt, marginTop:8 }}>{callTarget.fn} {callTarget.ln}</div>
              {callTarget.title && <div style={{ fontSize:11, color:T.tx3 }}>{callTarget.title}</div>}
              {callTarget.co && <div style={{ fontSize:11, color:T.tx3 }}>{callTarget.co}</div>}
            </div>
            <div style={{ fontSize:9, fontWeight:400, color:T.tx3, letterSpacing:".1em", textTransform:"uppercase", marginBottom:6 }}>Contact Info</div>
            {callTarget.ph && <div style={{ fontSize:12, color:T.txt, marginBottom:4, fontFamily:"inherit" }}>{callTarget.ph}</div>}
            {callTarget.email && <div style={{ fontSize:11, color:"#FAFAFA", marginBottom:8, wordBreak:"break-all" }}>{callTarget.email}</div>}
            <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:12 }}>
              {callTarget.score != null && <div style={{ padding:"2px 8px", borderRadius:6, fontSize:10, fontWeight:400, background:"rgba(255,255,255,0.06)", color:"#FAFAFA" }}>Score: {callTarget.score}</div>}
              {callTarget.leadStage && <div style={{ padding:"2px 8px", borderRadius:6, fontSize:10, fontWeight:400, background:"rgba(255,255,255,0.06)", color:"#FAFAFA" }}>{callTarget.leadStage}</div>}
            </div>
            {(() => { const ctDeals = deals.filter(d => d.ct === callTarget.id); return ctDeals.length > 0 && (<>
              <div style={{ fontSize:9, fontWeight:400, color:T.tx3, letterSpacing:".1em", textTransform:"uppercase", marginBottom:6 }}>Deals</div>
              {ctDeals.map(d => <div key={d.id} style={{ padding:"6px 8px", marginBottom:4, borderRadius:8, background:"rgba(255,255,255,0.05)", border:"1px solid rgba(217,219,221,0.08)" }}>
                <div style={{ fontSize:11, fontWeight:400, color:T.txt }}>{d.name}</div>
                <div style={{ fontSize:10, color:T.tx3 }}>{d.stage} · ${d.val?.toLocaleString()}</div>
              </div>)}
            </>); })()}
            {callTarget.ai && <><div style={{ fontSize:9, fontWeight:400, color:T.tx3, letterSpacing:".1em", textTransform:"uppercase", marginTop:10, marginBottom:4 }}>AI Notes</div><div style={{ fontSize:11, color:T.tx2, lineHeight:1.5 }}>{callTarget.ai}</div></>}
            <button onClick={()=>setCallTarget(null)} style={{ width:"100%", marginTop:12, padding:"6px 0", borderRadius:8, border:"1px solid rgba(217,219,221,0.12)", background:"transparent", fontSize:10, color:T.tx3, cursor:"pointer", fontFamily:"inherit" }}>Hide Context</button>
          </div>}
          <div style={{ flex:1, overflow:"hidden", height:"100%" }}><MCTheme><DialerPage /></MCTheme></div>
        </div>
      </SystemModal></div>}
      {modal==="callLogs"&&<SystemModal title="Call Logs" onClose={()=>setModal(null)} size="large"><CallLogsPage /></SystemModal>}
      {modal==="email"&&<SystemModal title="Email" onClose={()=>setModal(null)} size="large"><EmailPage /></SystemModal>}
      {modal==="sms"&&<SystemModal title="SMS" onClose={()=>setModal(null)} size="large"><SMSPage /></SystemModal>}
      {modal==="calendar"&&<SystemModal title="Calendar" onClose={()=>setModal(null)} size="large"><CalendarPage /></SystemModal>}
      {modal==="teamChat"&&<SystemModal title="Team Chat" onClose={()=>setModal(null)} size="large"><MCTheme><TeamChatPage onSelectContact={handleMCContact} onSelectDeal={handleMCDeal} /></MCTheme></SystemModal>}
      {modal==="projects"&&<SystemModal title="Projects" onClose={()=>setModal(null)} size="large"><MCTheme><ProjectsPage /></MCTheme></SystemModal>}
      {modal==="workflows"&&<SystemModal title="Workflows" onClose={()=>setModal(null)} size="large"><MCTheme><WorkflowsPage /></MCTheme></SystemModal>}
      {modal==="newTask"&&<CenterModal title="Create Task" onClose={()=>setModal(null)} width={440}><NewTaskPanel contacts={contacts} onClose={()=>setModal(null)} /></CenterModal>}

      {/* ═══ DISAMBIGUATION PICKER ═══ */}
      {disambig && <CenterModal title="Which contact?" onClose={()=>setDisambig(null)} width={400}>
        <div style={{ padding: 16 }}>
          <div style={{ fontSize: 12, color: T.tx3, marginBottom: 12 }}>Multiple contacts matched. Tap to select:</div>
          {disambig.contacts.slice(0, 8).map((c, i) => (
            <button key={c.id} onClick={() => {
              setDisambig(null);
              // Re-run the original intent with this specific contact
              const intent = disambig.intentId;
              if (intent === "call_contact") { setCallTarget(c); store.setCallQueue(prev => [{ ...c, ct: c.id, fn: c.fn, ln: c.ln, ph: c.ph, co: c.co, title: c.title, attempts: 0, id: c.id }, ...prev.filter(q => q.ct !== c.id)]); setModal("dialer"); speak(`Calling ${c.fn} ${c.ln}.`); window.__coreflow_auto_dial = { phone: c.ph || c.phone, contact: c }; }
              else if (intent === "email_contact" || intent === "draft_followup" || intent === "draft_outreach" || intent === "draft_breakup" || intent === "draft_proposal") { setSelectedContact(c); setModal("email"); speak(`Opening email for ${c.fn} ${c.ln}.`); }
              else if (intent === "text_contact") { setSelectedContact(c); setModal("sms"); speak(`Opening SMS for ${c.fn} ${c.ln}.`); }
              else if (intent === "show_contact") { setSelectedContact(c); speak(`Pulling up ${c.fn} ${c.ln}.`); }
              else if (intent === "add_to_queue") { store.addToQueue(c); speak(`Added ${c.fn} ${c.ln} to queue.`); }
              else if (intent === "schedule_meeting") { setSelectedContact(c); setModal("calendar"); speak(`Scheduling with ${c.fn}.`); }
              else if (intent === "create_task_for") { store.addTask({ title: `Follow up with ${c.fn} ${c.ln}`, due: new Date().toISOString().slice(0,10), pri: "medium", contactId: c.id }); speak(`Created task for ${c.fn}.`); }
              else if (intent === "precall_prep" || intent === "conversation_history" || intent === "questions_to_ask") {
                setSelectedContact(c);
                const ctDeals = deals.filter(d => d.ct === c.id);
                const ctActs = (store.getActivitiesForContact?.(c.id) || []).slice(0, 8);
                sendCoreMsg(`Pre-call briefing for ${c.fn} ${c.ln}`, `Pre-call briefing for ${c.fn} ${c.ln}, ${c.title||""} at ${c.co||""}. Score: ${c.score}. Deals: ${JSON.stringify(ctDeals.map(d=>({name:d.name,stage:d.stage,val:d.val})))}. Activities: ${JSON.stringify(ctActs.map(a=>({type:a.type,date:a.date})))}. Give opener, talking points, objections, next step.`);
              }
              else { setSelectedContact(c); speak(`Selected ${c.fn} ${c.ln}.`); }
              setCoreMessages(p => [...p, { role: "core", text: `Got it — ${c.fn} ${c.ln} at ${c.co || ""}. ${c.title || ""}` }]);
            }}
            style={{
              width: "100%", display: "flex", alignItems: "center", gap: 12,
              padding: "12px 14px", marginBottom: 6, borderRadius: 8,
              border: "1px solid rgba(0,0,0,0.20)", background: "rgba(255,255,255,0.06)",
              cursor: "pointer", fontFamily: "inherit", textAlign: "left",
              transition: "all .15s cubic-bezier(.32,1,.23,1)",
            }}
            onMouseEnter={e => { e.currentTarget.style.background = `${A}08`; e.currentTarget.style.borderColor = `${A}25`; e.currentTarget.style.transform = "translateY(-1px)"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.06)"; e.currentTarget.style.borderColor = "rgba(0,0,0,0.20)"; e.currentTarget.style.transform = "none"; }}
            >
              
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 400, color: T.txt }}>{c.fn} {c.ln}</div>
                <div style={{ fontSize: 11, color: T.tx3 }}>{c.title ? c.title + " — " : ""}{c.co || ""}</div>
                {c.email && <div style={{ fontSize: 10, color: T.tx3, marginTop: 1 }}>{c.email}</div>}
              </div>
              <div style={{ textAlign: "right" }}>
                {c.score && <div style={{ fontSize: 11, fontWeight: 400, color: c.score >= 80 ? "#2E8B57" : c.score >= 50 ? "#D4763A" : T.tx3 }}>{c.score}</div>}
                {c.ph && <div style={{ fontSize: 9, color: T.tx3, fontFamily: "inherit" }}>{c.ph}</div>}
              </div>
            </button>
          ))}
        </div>
      </CenterModal>}

      {/* ═══ ONBOARDING OVERLAY ═══ */}
      {showOnboarding && <div style={{ position:"fixed", inset:0, zIndex:3000, display:"flex", alignItems:"center", justifyContent:"center" }}>
        <div onClick={dismissOnboarding} style={{ position:"absolute", inset:0, background:"rgba(0,0,0,0.60)", backdropFilter:"blur(30px)" }} />
        <div style={{ position:"relative", width:640, maxWidth:"92vw", background:"#2C2E33", border:"1px solid rgba(217,219,221,0.08)", borderRadius:12, overflow:"hidden", boxShadow:"0 50px 120px rgba(0,0,0,0.5)", animation:"imFade .5s ease" }}>
          <div style={{ padding:"32px 36px 20px", textAlign:"center" }}>
            <img src="/coreflow-logo-white.png" alt="CoreFlow" style={{ height: 24, objectFit: "contain", marginBottom: 20 }} />
            <div style={{ fontSize:28, fontWeight:400, color:"#FAFAFA", letterSpacing:"-0.03em", marginBottom:4 }}>Mission Control</div>
            <div style={{ fontSize:12, color:"rgba(217,219,221,0.5)", marginTop:8, lineHeight:1.6 }}>Your command center. Everything you need, one voice command away.</div>
          </div>
          <div style={{ padding:"0 36px 24px" }}>
            {[
              { icon:"⎵", title:"Hold Space to Talk", desc:"Press and hold the spacebar to give Core a voice command. Release to send." },
              { icon:"⌨", title:"Type Commands", desc:"Use the chat bar at the bottom to type anything. Questions, actions, research." },
              { icon:"◈", title:"Quick Access", desc:"Use the action bar on the right to open any tool. Dialer, Email, SMS, Calendar, and more." },
              { icon:"◎", title:"Ask Anything", desc:"Research industries, draft emails, analyze your pipeline, prep for calls. Core handles it all." },
            ].map((item, i) => (
              <div key={i} style={{ display:"flex", gap:14, padding:"12px 0", borderTop:i>0?"1px solid rgba(217,219,221,0.06)":"none" }}>
                <div style={{ width:36, height:36, borderRadius:8, background:"rgba(64,89,173,0.1)", border:"1px solid rgba(64,89,173,0.15)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:15, flexShrink:0, color:"#FAFAFA" }}>{item.icon}</div>
                <div><div style={{ fontSize:13, fontWeight:400, color:"#FAFAFA" }}>{item.title}</div><div style={{ fontSize:11, color:"rgba(217,219,221,0.4)", marginTop:2, lineHeight:1.5 }}>{item.desc}</div></div>
              </div>
            ))}
          </div>
          <div style={{ padding:"0 36px 28px", display:"flex", gap:8 }}>
            <button onClick={async () => { await requestMicPermission(); dismissOnboarding(); }} style={{ flex:1, padding:"12px 0", borderRadius:8, border:"none", background:"#4059AD", color:"#D9DBDD", fontSize:12, fontWeight:400, cursor:"pointer", fontFamily:"inherit" }}>Enable Voice & Get Started</button>
            <button onClick={dismissOnboarding} style={{ padding:"12px 20px", borderRadius:8, border:"1px solid rgba(217,219,221,0.12)", background:"transparent", color:"rgba(217,219,221,0.5)", fontSize:11, fontWeight:400, cursor:"pointer", fontFamily:"inherit" }}>Skip</button>
          </div>
        </div>
      </div>}

      {/* ═══ CONTACT / DEAL MODALS ═══ */}
      {selectedContact&&<SystemModal title={`${selectedContact.fn} ${selectedContact.ln}`} onClose={()=>setSelectedContact(null)} size="large"><MCTheme><ContactRecord contact={selectedContact} onBack={()=>setSelectedContact(null)} onSelectDeal={(d)=>{setSelectedContact(null);handleMCDeal(d);}} /></MCTheme></SystemModal>}
      {selectedDealId&&<SystemModal title="Deal" onClose={()=>setSelectedDealId(null)} size="large"><MCTheme><DealRecord dealId={selectedDealId} onBack={()=>setSelectedDealId(null)} onSelectContact={(c)=>{setSelectedDealId(null);handleMCContact(c);}} /></MCTheme></SystemModal>}
    </div>
  );
}

function NewTaskPanel({contacts,onClose}){const store=useStore();const [t,sT]=useState("");const [du,sDu]=useState(new Date().toISOString().slice(0,10));const [p,sP]=useState("medium");const create=()=>{if(!t.trim())return;store.createTask({title:t,due:du,pri:p});onClose();};const fi={width:"100%",padding:"10px 14px",borderRadius: 8,border:"1px solid rgba(217,219,221,0.12)",fontSize:13,outline:"none",background:"rgba(255,255,255,0.06)",fontFamily:"inherit"};const lb={fontSize:10,fontWeight:400,color:T.tx3,letterSpacing:".1em",textTransform:"uppercase",marginBottom:4};return<div style={{padding:20}}><div style={lb}>Task</div><input value={t} onChange={e=>sT(e.target.value)} placeholder="Follow up with..." style={{...fi,marginBottom:10}} autoFocus/><div style={{display:"flex",gap:8,marginBottom:10}}><div style={{flex:1}}><div style={lb}>Due</div><input type="date" value={du} onChange={e=>sDu(e.target.value)} style={fi}/></div><div style={{flex:1}}><div style={lb}>Priority</div><div style={{display:"flex",gap:4}}>{["low","medium","high"].map(pr=>(<button key={pr} onClick={()=>sP(pr)} style={{flex:1,padding:"7px 0",borderRadius:8,border:p===pr?"1.5px solid "+(pr==="high"?"#E53935":pr==="medium"?"#D4763A":A)+"40":"1px solid rgba(217,219,221,0.12)",background:p===pr?(pr==="high"?"#E53935":pr==="medium"?"#D4763A":A)+"08":"transparent",color:p===pr?(pr==="high"?"#E53935":pr==="medium"?"#D4763A":A):T.tx3,fontSize:10,fontWeight:400,cursor:"pointer",fontFamily:"inherit",textTransform:"capitalize"}}>{pr}</button>))}</div></div></div><button onClick={create} style={{width:"100%",padding:"10px 0",borderRadius: 8,border:"none",background:A,color:"#D9DBDD",fontSize:12,fontWeight:400,cursor:"pointer",fontFamily:"inherit"}}>Create Task</button></div>;}
